/**
 * useLocaleTexts Hook
 * 
 * Provides locale-specific text overrides for editorial content, badges,
 * and cultural adaptations. Falls back to US English defaults when no
 * override exists for the current locale.
 * 
 * Text key convention:
 *   badge.shipping          → "Free Shipping" / "Free Returns"
 *   badge.origin            → "Made in USA" / "Award-Winning Quality"
 *   badge.trial             → "30-Night Better Sleep"
 *   editorial.currency_symbol → "$" / "£" / "€" / "A$"
 *   editorial.hotel_price_range → "$150–$200" / "£120–£160"
 *   editorial.hotel_price_single → "$200" / "£160"
 *   editorial.competitor_price_prefix → "$" / "£"
 *   spelling.customize      → "customize" / "customise"
 *   spelling.favorite       → "favorite" / "favourite"
 *   shipping.free_text      → "Free shipping" / "Free returns"
 *   shipping.subtext        → "Free shipping · 30-night trial" / "Free returns · 30-night trial"
 */
import { useMemo } from 'react';
import { useLocale } from '@/contexts/LocaleContext';
import { trpc } from '@/lib/trpc';

// ─── US Defaults (fallback when no locale override exists) ──────────────────

const US_DEFAULTS: Record<string, string> = {
  // Badges
  'badge.shipping': 'Free Shipping',
  'badge.origin': 'Made in USA',
  'badge.trial': '30-Night Better Sleep',

  // Editorial currency/prices (used in contextual copy, not product pricing)
  'editorial.currency_symbol': '$',
  'editorial.hotel_price_range': '$150–$200',
  'editorial.hotel_price_single': '$200',
  'editorial.hotel_price_four_seasons': '$200',
  'editorial.hotel_price_ritz': '$140',
  'editorial.hotel_price_marriott': '$130',
  'editorial.competitor_purple': '$159',
  'editorial.competitor_coop': '$72',
  'editorial.competitor_saatva': '$89',
  'editorial.competitor_beckham': '$45',
  'editorial.big_box_pillow': '$20',
  'editorial.luxury_pillow_range': '$140–$350',
  'editorial.therapeutic_pillow': '$200+',
  'editorial.manufacture_cost': '$18–22',
  'editorial.distributor_cost': '$35–40',
  'editorial.rent_staff_cost': '$55–70',
  'editorial.retail_markup': '$80–100',
  'editorial.retail_final': '$140–350',
  'editorial.pillow_annual_cost': '$12–20/year',
  'editorial.luxury_pillow_180': '$180',
  'editorial.luxury_pillow_range_bold': '$150–$200+ EACH',
  'editorial.hotel_gift_shop_range': '$130–$200',
  'editorial.hotel_procurement': '$18–22',
  'editorial.distributor_price': '$35–40',
  'editorial.factory_cost': '$18–22',
  'editorial.fluffco_per_year': '$12–20/year',
  'editorial.chiro_annual': '$14,000',
  'editorial.cervical_pillow_annual': '$200+',
  'editorial.pain_meds_annual': '$500+/year',
  'editorial.traction_device': '$300+',

  // Brand
  'brand.made_in': 'Made in USA',

  // Shipping text
  'shipping.delivery_time': '2-5 Business Days',
  'shipping.free_text': 'Free shipping',
  'shipping.subtext': 'Free shipping · 30-night trial · Full refund guarantee',
  'shipping.subtext_alt': 'Free shipping & returns · Full refund if you\'re not sleeping better',
  'shipping.online_pricing': 'Online-only pricing. Free shipping on all orders.',

  // Spelling (American English defaults)
  'spelling.customize': 'customize',
  'spelling.favorite': 'favorite',
};

export interface LocaleTextsResult {
  /** Get a locale-specific text by key, falling back to US default */
  t: (key: string, fallback?: string) => string;
  /** Whether the locale text data is still loading */
  isLoading: boolean;
  /** Whether we're on an international locale (non-US) */
  isInternational: boolean;
  /** The raw text map for direct access */
  texts: Record<string, string>;
}

export function useLocaleTexts(): LocaleTextsResult {
  const { locale, isInternational } = useLocale();

  // Only fetch locale texts when on an international locale
  const { data: localeTextsData, isLoading } = trpc.localeTexts.getByLocale.useQuery(
    { localeId: locale.id! },
    { enabled: isInternational && !!locale.id }
  );

  // Build a merged text map: locale overrides take precedence over US defaults
  const texts = useMemo(() => {
    const merged = { ...US_DEFAULTS };
    if (localeTextsData) {
      for (const item of localeTextsData) {
        merged[item.textKey] = item.value;
      }
    }
    return merged;
  }, [localeTextsData]);

  // Translation function with fallback chain: locale override → US default → custom fallback
  const t = useMemo(() => {
    return (key: string, fallback?: string): string => {
      return texts[key] ?? fallback ?? US_DEFAULTS[key] ?? key;
    };
  }, [texts]);

  return {
    t,
    isLoading,
    isInternational,
    texts,
  };
}
